import { api } from "@/shared/api";
import { rememberVideoModelLabels } from "@/shared/brand/engineTiers";
import { type OverlayConfig } from "@/shared/lib/overlay/overlayConfig";
import type { OrgScopeParams } from "@/shared/hooks/useOrgScopeFilter";
import type { MediaTaskStatus } from "@/shared/lib/taskStatus";

// AI视频模块 (V38, 2026-05-28) — 3 provider 用户显式可选

export type VideoTaskType = "PROMO" | "DRAMA";
export type VideoSource = "FREE" | "TEMPLATE" | "FROM_POSTER";
/**
 * 视频任务状态 = `media_task.status`,与海报/AI分身**同一列、同一套词汇**。
 *
 * <p>2026-07-16 收口到 {@link MediaTaskStatus}(shared/lib/taskStatus)。此前这里是自己一份联合类型:
 * **漏了后端真正会产的 RUNNING**,却列着一串后端根本不产的 legacy 细分态
 * (SCRIPTING/COMPOSING/SAFETY_CHECKING/RENDERING/DOWNLOADING/POST_SAFETY)。
 * 代价不是几行没用的字符串:三处「在途判定」照着这个错类型各写一份本地 Set、全都漏 RUNNING
 * → worker 一认领任务,列表和详情页的轮询集体停摆。
 */
export type VideoStatus = MediaTaskStatus;
/** 单镜编辑(Step2a):改某镜文本字段;undefined/null = 不改。panelScene(首帧)待 Step2b。 */
export interface VideoShotEdit {
  /** 画面描述 → 后端写 panelScenes。★名字是历史包袱,别当"最终 prompt"用。 */
  panelPrompt?: string | null;
  /** 运动/动作 → 后端写 panelMotions(2026-08-18 补:在此之前动作根本改不了)。 */
  panelMotion?: string | null;
  voiceover?: string | null;
  negativePrompt?: string | null;
}

export type VideoProvider = "seedance" | "happyhorse" | "wan2.7";
/**
 * `R2V_SWAP`(SEEDANCE 真人出镜 P0.5,0915 车 F/G 契约「换我出镜」):参考视频换脸 ——
 * 场景/运镜完全跟参考视频走,出镜人换成选定的真人人物。真人出镜专属页
 * (`VideoRealCreatePage`)专属,无模型选择、时长跟随参考视频(≤15s)。
 * 与 `swapRefKey`(下方 `VideoTaskCreateRequest`)成对:此模式下 swapRefKey 必填。
 */
export type GenerationMode = "T2V" | "I2V" | "I2V_FIRST_LAST" | "R2V_SWAP";
export type VideoResolution = "480p" | "720p" | "1080p";

export interface VideoProviderCapability {
  provider: VideoProvider;
  model: string;
  /**
   * 对客档位名(「专业·综合」)—— OEM H2 起后端直出,单一真源 api `VideoModelTiers`;
   * 不含厂商 / 模型名,直接展示。
   */
  label: string;
  /** 能力与适用场景一句话(不含厂商 / 榜单宣传词)。 */
  tagline: string;
  /** 质量档(旗舰 / 专业 / 标准 / 极速);老后端无此键。 */
  tier?: string | null;
  /** 质量档排序位(越小越高档);老后端无此键。 */
  tierRank?: number | null;
  supportedResolutions: VideoResolution[];
  supportedRatios: string[];
  durationRange: [number, number];
  supportedGenerationModes: GenerationMode[];
  supportsGenerateAudio: boolean;
  supportsNegativePrompt: boolean;
  supportsPromptExtend: boolean;
  supportsReturnLastFrame: boolean;
  realFaceRestricted: boolean;
  /**
   * 2026-05-30 统一积分计费 — 来自 xb_ai_model_credit_rate(跟出片实扣同源)。
   * creditsPerSecond:该 model 1080p 基准每秒积分;frameImageCredits:每镜首帧出图积分(= 海报 gpt-image-2);
   * resolutionMultipliers:分辨率系数(1080p=1/720p=0.36/480p=0.23)。前端按这三者估算「积分」= 实扣。
   */
  creditsPerSecond?: number | null;
  frameImageCredits?: number | null;
  resolutionMultipliers?: Record<string, number> | null;
}

export interface VideoCapabilitiesResponse {
  providers: VideoProviderCapability[];
  maxConcurrent: number;
  schemaVersion: string;
}

/**
 * T2A 上传图实体(分类器 compose 前产 + 确认单可纠)。wire camelCase(productId),
 * 与 ai 侧 Entity / VideoTaskRequest 逐字对齐。图背书实体带 key(i2i 种子);文本背书 key 空。
 */
export interface VideoEntity {
  id: string;
  role: "product" | "character" | "logo" | "scene" | "other";
  source: "image" | "text";
  key?: string;
  productId?: string;
  name?: string;
  confidence?: number;
  /**
   * 波3-T4:true = 这个实体是 ai compose 层自动合成的(文案命中产品 + 产品树有预置图),
   * 不是用户显式选/传的。来源说明据此写成「已自动使用」并提示可移除 —— 自动注入必须可见可撤。
   */
  auto?: boolean;
}

/**
 * W2/W3 逐镜参考派活(references[i] = 该镜的参考清单)。wire camelCase,与 ai 侧
 * ShotReference(models.py)逐字对齐:assetRef = 实体 id / MinIO objectKey / http(s) 直链;
 * role = image|reference_video|reference_audio;job = identity|motion|style_transfer|lip_sync…;
 * durationSec 由 backend 从资产表带出(ai 编译层 15.2s 墙靠它挡,选库素材必带)。
 */
export interface VideoShotReference {
  assetRef: string;
  role: string;
  job?: string;
  durationSec?: number;
}

export interface VideoTaskCreateRequest {
  /**
   * 批 L:确认卡上那张**正式报价单**的单号(`xb_price_quote.quote_id`)。
   *
   * ★ 后端按 `(tenant, quoteId)` 把单绑到 action_id,结算时按单兑现。
   *   不带的话这一单会落一条**无主的 MEDIA settle** —— 后端目前软着陆(打
   *   `*_task_without_quote` WARN 照常出片),前端全切完之后会改成硬拒。
   * ★ 显式声明在这里,不靠对象展开塞进去:字段名打错时**没有任何东西会红** ——
   *   请求照发、后端照收、quoteId 恒 null、WARN 照打,而 WARN 没人天天看。
   */
  quoteId?: string;
  /** B6 场景卡打标(catalog sceneKey;缺省=不打标)。 */
  sceneKey?: string | null;
  name?: string;
  type: VideoTaskType;
  source: VideoSource;
  sourceRef?: Record<string, unknown>;
  sourceTaskId?: number;
  templateId?: number;
  userInput: string;
  styleCode?: string;
  /** 风格描述(预设 tone 或自定义文本),落 requestPayload 供详情/列表回显用户选的风格。 */
  briefStyle?: string;
  industryCode?: string;
  provider: VideoProvider;
  model: string;
  generationMode: GenerationMode;
  resolution: VideoResolution;
  ratio: string;
  durationSec: number;
  generateAudio: boolean;
  /**
   * 单镜首帧 = 海报参考图(只有 FROM_POSTER 路会有值)。带签名、会过期。
   *
   * 它的**稳定 ref 是 sourceRef.fromPosterTaskId + fromPosterAssetSeq**(已随请求落库),
   * 后端 retry 时据此按租户重新解析海报产物并现签 —— 所以这里不需要额外送 key。
   */
  firstFrameUrl?: string;
  lastFrameUrl?: string;
  /**
   * 0924 车 B(契约 §3.3):自由创作上传尾帧的稳定 key —— lastFrameUrl 是会过期的签名,
   * api 派发/retry 时按它现签并过租户闸(首帧的 key 走 `panelFirstFrameKeys[0]`)。
   */
  lastFrameKey?: string;
  negativePrompt?: string;
  promptExtend?: boolean;
  /** V42:平台 BGM id(null=无 BGM 或走 customBgmObjectKey) */
  bgmId?: number;
  /** V42:用户自传 BGM 的 MinIO key;bgmId 优先 */
  customBgmObjectKey?: string;
  /**
   * ★契约 v2 基准数组(2026-07-16):每镜环境描述(≤15 字中文)。长度 = 镜数。
   * 取代已删的 panelPrompts —— 导演不再产 prompt 文本,最终 prompt 由 ai 编译层单点生成。
   */
  panelScenes?: string[];
  /** 每镜主体动作(≤20 字中文,一个动作)。与 panelScenes 并列,是导演仅有的两个自由文本之一。 */
  panelMotions?: string[];
  /** 每镜主体指代(指向 subjects 的 @token);编译期展开成描述,@token 不进模型。 */
  panelSubjectRefs?: string[];
  /** 新主线:每镜独立首帧图 key(锚点 i2i 生成);每镜 I2V 用自己的首帧,对齐 panelScenes */
  panelFirstFrameUrls?: string[];
  /** 每镜首帧的稳定 key(对齐 panelFirstFrameUrls);理由同 firstFrameKey —— 供 retry 现签 */
  panelFirstFrameKeys?: string[];
  /** 每镜生成模式 T2V/I2V;ai-service 据此逐镜分流(T2V 镜不出首帧、纯文生视频) */
  shotMode?: string[];
  /** 一句话视频主题,落库供详情页回显 */
  theme?: string;
  /** V42.x LLM 推断每镜独立时长 */
  panelDurations?: number[];
  /** V42.x LLM 推断每镜独立负面词(透给 video provider) */
  negativePrompts?: string[];
  /** V42.x LLM 推断每镜口播文案(ffmpeg burn-in SRT + TTS audio 双轨) */
  voiceovers?: string[];
  /** V42.x CosyVoice 音色 id;TTS 合成口播 audio 用 */
  voiceTone?: string;
  /** V42.x 整片基调情感 happy/neutral/...;仅情感可控音色生效 */
  voiceEmotion?: string;
  /** 每镜口播情感(逐镜分开 TTS 配语气);与 voiceovers 等长 */
  voiceEmotions?: string[];
  /** V42.x 品牌识别关键词;字幕里金色高亮 */
  brandFocus?: string;
  /** V42.x 字幕字体 key(heiti/songti/kaiti/yuanti) */
  fontStyle?: string;
  /** V42.x 口播语速 0.9-1.3 */
  voiceSpeechRate?: number;
  /** V42.x 口播音量 30-90 */
  voiceVolume?: number;
  /** V42.x 多镜转场 hard/fade */
  transition?: string;
  /** 字幕大小 small/medium/large(用户可选,默认 medium) */
  subtitleFontScale?: string;
  /** 字幕位置 bottom/center/top(用户可选,默认 bottom) */
  subtitlePosition?: string;
  /** P2d-1 视频目的(产品带货/获客/品牌/...);驱动 ai 端 grade。 */
  purpose?: string;
  /**
   * 结构化运镜/景别/布光/叙事(导演只填受控枚举,措辞由 ai 侧 prompt_lexicon_zh 单点投影)。
   * ★契约 v2 已删 lensMm/depthsOfField/colorTemperatures/textureKeywords —— 那是导演逐镜自由
   * 发挥的入口(英文堆砌之源),风格改由用户选的 styleCode 一次定调。
   */
  shotSizes?: string[];
  cameraMovements?: string[];
  /** 每镜运镜速度 slow/brisk/punchy;与方向拆开 = 「堆两个运镜」在结构上无法表达。static 镜给 ""。 */
  cameraSpeeds?: string[];
  lightingKeys?: string[];
  shotIntents?: string[];
  narrativeRoles?: string[];
  heroMoments?: boolean[];
  /** 批1 连续性:per-shot 场景标签/衔接声明 + 主体注册表(纯透传,承接门在 ai render 读)。 */
  sceneIds?: string[];
  shotContinuities?: string[];
  subjects?: string[];
  /** 批2 hook 三轨落档(storyboard 级,纯透传)。 */
  hookPlan?: Record<string, unknown>;
  /** item1 事实→画面 通路(per-shot;hero 镜 ai 编译期用 factVisual 取代 subjectMotion)。 */
  factRefs?: string[][];
  factVisuals?: string[];
  /** T2A 上传图实体表 + 单产品图便捷位(ai 模式路由器按图背书产品实体选 i2i-I2V)。 */
  entities?: VideoEntity[];
  productRefKey?: string;
  /** W3:逐镜口播三态(voiceover|dialogue|ambient)+ 参考派活(缺=老任务,ai 侧归一兜底)。 */
  speechModes?: string[];
  references?: VideoShotReference[][];
  /**
   * SEEDANCE 真人出镜 P0.5「换我出镜」(0915 车 F/G 契约,逐字同拼):参考视频的
   * objectKey——`generationMode:"R2V_SWAP"` 时必填,其余模式不读。
   * 与真人身份(entities 里的 LIBC 真人实体)成对:api 侧校验 R2V_SWAP 必须是真人任务。
   */
  swapRefKey?: string;
  /**
   * SEEDANCE 真人出镜 P0.5(0915 车 F 契约钉死批,G5 已修三面断链):直发单镜(专属页
   * 「文案出片」/「换我出镜」都不经编排)此前**没有**开关能表达"这次要不要配音/字幕/BGM"——
   * `voiceovers`/`subtitleFontScale`/`bgmId` 这些字段过去只在**编排后(幕二)**由 ai 预填
   * 才被消费。G5 批修完这三面断链后,createTask 的直发路径也认这三个开关了,字段名与
   * `VideoComposeOptions`(成片重合成选项)**逐字同名**——同一件事只留一套名字。
   * 三者独立:`voiceEnabled` 配 `voiceovers`/`speechModes`(取长度 1 的逐镜数组当单镜用),
   * `subtitleEnabled` 配 `subtitleFontScale`,`bgmEnabled` 配 `bgmId`/`customBgmObjectKey`。
   */
  voiceEnabled?: boolean;
  subtitleEnabled?: boolean;
  bgmEnabled?: boolean;
  /**
   * SEEDANCE 真人出镜 P0.6(0915 任务书车 V,契约钉死):出镜人自己的克隆声纹
   * (`character.voiceProfileId`)。仅口播开启时有意义;**语义钉死** —— 在场则口播合成用
   * 该克隆声纹、`voiceTone` 被忽略(优先级钉死,api/ai 两端都不读 voiceTone),不在场
   * 才走现行 `voiceTone` 预置音色。api 侧校验声纹可用(跨公司/不存在/已下架拒);授权入库后全公司共用,
   * 录音克隆须本人、删除仅本人或管理员。
   */
  voiceProfileId?: number;
  /**
   * 声音 V2 三件套(P0.6 0915 车 I:真人页接入,词表权威源
   * `entities/dh-profile/model/voiceV2Options.ts`,与数字人页 `DhTaskCreateRequest` 同口径)。
   * **仅 `voiceProfileId` 在场时有意义**——预置音色(`voiceTone`)是 cosyvoice 旧引擎,不认
   * 这三个参数;带了也被后端**静默忽略**(主session 0915 演示机实证:presetVoice +
   * voiceDialect=sichuan 仍返 200 成功音频,只是方言没生效,不报错)。真人页只在选中
   * 「我的声纹」时才发这三个字段,选预置音色时整段不发(与 voiceProfileId 同一条纪律)。
   */
  voiceLanguage?: string;
  /** 仅 voiceLanguage="zh" 可非空;null=跟随克隆样本原声。契约见 DH_VOICE_DIALECTS 头注。 */
  voiceDialect?: string | null;
  voiceStyle?: string;
  /**
   * SEEDANCE 真人出镜 P0.6(0915 任务书车 V):「保留参考视频原声」。仅
   * `generationMode:"R2V_SWAP"`(换我出镜)有意义 —— true 时 ai 成片合成阶段把参考视频
   * (`swapRefKey`)自身的原声/环境音混进成片;false/缺省=不保留(现状)。非 R2V_SWAP
   * 任务带了它,api/ai 两端都宽容忽略,不校验、不报错。
   */
  keepSourceAudio?: boolean;
  /**
   * 第三方视频换脸合规口(0925 收尾波 D2):用户在「换我出镜」参考视频上传处勾选
   * 「我确认对该视频拥有使用权并已获得出镜者授权」后才发 true。仅
   * `generationMode:"R2V_SWAP"` 有意义,前端未勾选时提交按钮本身就禁用,不会发这个字段。
   * ★api 本波未接:record 没加这个字段,`@JsonIgnoreProperties` 会静默丢掉、不落库——
   *   前端先发,等 api 补上字段再落库/校验(不因为 api 没接就不发)。
   */
  swapUsageRightConfirmed?: boolean;
  /**
   * 这一单要不要把出镜人物的**身份视频**也挂进参考集(0915 归一批 B「身份参考可选」)。
   *
   * 缺省/false = 只用照片。vin 与 novin 单价完全一样,差别只在计量
   * (qty = 出片秒 + 身份视频秒),所以这是一个**成本**开关,不是画质开关。
   * ★后端同步用它收窄就绪判据:false 时不等身份视频 active —— 否则会出现
   * "我只想用照片,却被一条还在注册中的视频挡住出不了片"。
   *
   * ★0920 起它只是 {@link identityRefMode} 的**兼容镜像**(= 这一档带不带视频),
   *   新客户端两个都发;api 侧两者都给时以 identityRefMode 为准。不退役的理由:
   *   存档 request_payload 的重渲、军师 slot、老客户端都还在发它。
   */
  identityUseVideo?: boolean;
  /**
   * 身份参考档(2026-09-20,用户反馈「只能选照片,不能单选视频」)。逐字三档:
   * `photo`(只用定妆照,缺省)/ `photo_video`(定妆照 ＋ 身份视频)/ `video`(**仅身份视频**,
   * 不挂定妆照)。
   *
   * ★为什么不是再加一个布尔:上面那个布尔的两个值都带着照片,表达不了「这一单就照着我这段
   *   视频来,别拿照片」;再加第二个布尔会多出「不要图也不要视频」这种不成立的组合。
   * ★计费口径不变:两个带视频的档都是 vin、qty 完全相同(vapeur 只要收到视频输入就走 vin),
   *   差别只在送哪些参考。
   * ★api 侧:人物没有身份视频却传了 video/photo_video → 422
   *   REAL_PERSON_IDENTITY_VIDEO_MISSING(不静默退化成只用照片)。
   */
  identityRefMode?: "photo" | "photo_video" | "video";
  /**
   * 调性档(0924 车 C,契约 §3.1):hardsell 带货喊话 / restrained 品牌克制 / luxury 高奢静默。
   * 缺省 = ai 按行业码推。api 校验枚举(非法 422)后原样透传。见 `model/toneRegister.ts`。
   */
  toneRegister?: "hardsell" | "restrained" | "luxury" | null;
}

/** V42:DRAMA 任务参演角色 chip 数据(详情页用)。 */
export interface VideoCharacterRefDto {
  id: number;
  name: string;
  thumbUrl?: string;
}

export interface VideoAssetDto {
  id: number;
  taskId: number;
  seq: number;
  /** CHUNK(P2d-2):段=出片/展示资产单元;旧任务仍用 SHOT(每镜)。详情页按 kind 两态分支。 */
  kind: "FINAL" | "FINAL_CLEAN" | "SHOT" | "CHUNK";
  videoUrl?: string;
  thumbUrl?: string;
  lastFrameUrl?: string;
  width?: number;
  height?: number;
  durationSec?: number;
  sizeBytes?: number;
  hasAudio?: boolean;
  /**
   * V4(2026-07-16):分块计划**先于渲染**下发 —— 段数一定就落 N 条 PENDING,再逐段推进
   * PENDING → RUNNING → SUCCESS/FAILED。此前详情页循环的是「已出产物」,没出的段在 DOM 里
   * 根本不存在 → 三段的任务只列出一段(生产实测,用户报的第一个现象)。
   */
  status: "PENDING" | "RUNNING" | "SUCCESS" | "FAILED" | "BLOCKED";
  errorCode?: string;
  /** CHUNK 专用:该段含的全局镜号(1-based),详情页展示「含镜 X–Y」。 */
  shotSeqs?: number[];
  /**
   * 波3-⑥ 降级留痕(CHUNK):ai 在人像首帧被 provider 内容安全拒收、丢帧降 T2V 保出片时写。
   * degradedFrom = 原生成模式(如 "I2V");degradeReason = 归因(如 "input_image_rejected")。
   * null/缺省 = 没降级。三层链(ai 写 / api 解 / 这里显示)每层都有断言 —— 缺一层留痕就是假的。
   */
  degradedFrom?: string;
  degradeReason?: string;
  /**
   * VID-D(2026-08-18):失败段的**人话原因**,后端 MediaFailureReason 从 errorCode + provider
   * 拒绝原文翻出来。★认不出时后端回的是「原因还没查清」而不是猜一个 —— 猜错会让用户改错东西。
   * 只有失败段有;成功段为 null(成功单带一句"原因"会让人以为出了问题)。
   */
  failureReason?: string;
  generatedAt?: string;
  /**
   * 0925 通道亮账:这段实际出片走的通道(ai 原样值 "vapeur" / "ark",不改大小写)。
   * failoverFrom 非空 = 主通道 submit 不可用、改走了 channelUsed(值为原主通道)。
   * 老任务 / 不带通道的产物 → 缺省,前端不显示,不猜。
   */
  channelUsed?: string;
  failoverFrom?: string;
}

/** 任务元数据改动(PATCH /video/tasks/{id})。null/缺省 = 不改该字段(后端同口径)。 */
export interface UpdateVideoTaskMetaRequest {
  isFavorite?: boolean;
  name?: string;
}

export interface VideoTaskDto {
  /** B6 场景打标(历史任务 null)。 */
  sceneKey?: string | null;
  /**
   * 目的档(8 值 label,`briefPurpose.ts` 同一契约)—— 0813 后端列表/详情出参补的。
   * 作品卡拿它派生品类名(sceneKey 只有从场景卡进来的片才有);历史任务无此键。
   */
  purpose?: string | null;
  id: number;
  tenantCode: string;
  creatorUserId?: number;
  creatorUserName?: string;
  name: string;
  type: VideoTaskType;
  source: VideoSource;
  sourceRef?: Record<string, unknown>;
  templateId?: number;
  templateName?: string;
  status: VideoStatus;
  provider: VideoProvider;
  model: string;
  /** 对客模型档位名(OEM H2 后端直出,如「专业·综合」);老后端无此键 → 前端按 model 兜底。 */
  modelLabel?: string | null;
  generationMode: GenerationMode;
  resolution: VideoResolution;
  ratio: string;
  durationSec: number;
  generateAudio: boolean;
  requestPayload?: Record<string, unknown>;
  script?: Record<string, unknown>;
  promptFinal?: string;
  creditsUsed?: number;
  errorCode?: string;
  errorMessage?: string;
  startedAt?: string;
  finishedAt?: string;
  createdAt: string;
  assets?: VideoAssetDto[];
  /** V42:DRAMA 任务参演角色,详情页 chip 渲染。 */
  characters?: VideoCharacterRefDto[];
  /** V42:任务级 BGM id(null=无 BGM)。 */
  bgmId?: number | null;
  /** V42.x:列表 cover-first 缩图(FINAL thumb.jpg presigned;只 SUCCESS 任务有)。 */
  coverThumbUrl?: string;
  /**
   * 2026-07-15(三线列表对齐):收藏。is_favorite 列在共用的 xb_media_task 上早就存在(海报在用),
   * 视频只是从没透出来 —— 补的是接线,不是新字段。
   */
  isFavorite?: boolean;
  /** 成片是否过期:有镜在末次合成后被重渲、或还没合成过 → true,详情页提示「重新合成」。 */
  finalStale?: boolean;
}

export interface FromPosterBootstrapResponse {
  referenceImageUrl?: string;
  referenceImageKey?: string;
  promptHint?: string;
  title?: string;
  sellingPoints?: string[];
  styleCode?: string;
  brand?: Record<string, unknown>;
  suggestedRatio?: string;
  suggestedDuration?: number;
  suggestedProvider?: VideoProvider;
  suggestedModel?: string;
  suggestedMode?: GenerationMode;
  /** V42 海报多格漫画检测:0 = 单图,>=2 = N 格漫画 → 多镜链 */
  panelCount?: number;
  /** ★契约 v2 基准数组:每镜环境描述(≤15 字中文)。长度定镜数,取代已删的 panelPrompts。 */
  panelScenes?: string[];
  /** 每镜主体动作(≤20 字中文);导演仅有的两个自由文本之一。 */
  panelMotions?: string[];
  /** 每镜主体指代(@token,指向 subjects 注册表)。 */
  panelSubjectRefs?: string[];
  /** 新主线:海报自身 key,作为逐镜首帧 i2i 的锚点(让首帧贴海报风格) */
  anchorKey?: string;
  /** V42.x LLM 推断每镜独立时长(秒)。单图也是长度 1。 */
  panelDurations?: number[];
  /** V42.x LLM 推断每镜独立负面词。 */
  negativePrompts?: string[];
  /** V42.x LLM 提炼视频主题(详情页+视频创建页 banner 展示)。 */
  theme?: string;
  /** V42.x LLM 提炼每镜口播文案(后期 burn-in 字幕)。 */
  voiceovers?: string[];
  /** V42.x LLM 提炼品牌识别关键词。 */
  brandFocus?: string;
  /** V42.x LLM 推断 BGM 情绪(商务/温馨/欢快/励志/氛围/节庆/忧伤)。 */
  suggestedBgmMood?: string;
  /** V42.x LLM 推荐 CosyVoice 音色 id(longanhuan 等)。 */
  suggestedVoiceTone?: string;
  /** V42.x LLM 推断整片基调情感 happy/neutral/...;仅情感可控音色生效。 */
  voiceEmotion?: string;
  /** 每镜口播情感(逐镜分开 TTS 配语气);与 voiceovers 等长。 */
  voiceEmotions?: string[];
  /** V42.x LLM 推断字幕字体 key(heiti/songti/kaiti/yuanti)。 */
  suggestedFontStyle?: string;
  /** V42.x LLM 推断口播语速 0.9-1.3。 */
  voiceSpeechRate?: number;
  /** V42.x LLM 推断口播音量 30-90。 */
  voiceVolume?: number;
  /** V42.x LLM 推断多镜转场 hard/fade。 */
  transition?: string;
  /** 结构化运镜/景别/布光/叙事(导演产出受控枚举,bootstrap 回传透传到 createTask)。 */
  shotSizes?: string[];
  cameraMovements?: string[];
  cameraSpeeds?: string[];
  lightingKeys?: string[];
  shotIntents?: string[];
  narrativeRoles?: string[];
  heroMoments?: boolean[];
  /** 批1 连续性:per-shot 场景标签/衔接声明 + 主体注册表(bootstrap 回传透传到 createTask)。 */
  sceneIds?: string[];
  shotContinuities?: string[];
  subjects?: string[];
  /** 批2 hook 三轨落档(bootstrap 回传透传到 createTask)。 */
  hookPlan?: Record<string, unknown>;
  /** item1 事实→画面(compose 回传 → 并入 form → createTask 透传给 ai 编译器)。 */
  factRefs?: string[][];
  factVisuals?: string[];
  /** W3:逐镜口播三态 + 参考派活(compose 回传 → 并入 form → createTask 透传)。 */
  speechModes?: string[];
  references?: VideoShotReference[][];
  primaryProduct?: string;
  /** T2A 上传图实体表(compose 回传:分类后的实体 → 确认单展示「产品=图X」+ createTask 透传)。 */
  entities?: VideoEntity[];
  /** 故事引擎:架构师产出的故事圣经,供「故事大纲」折叠展示。 */
  storyBible?: {
    logline?: string;
    protagonist?: { name?: string; appearance?: string; persona?: string };
    beats?: string[];
  } | null;
}

/**
 * 波2「选拍法」数据源(`GET /video/creative-taxonomy`)—— 目的 → 该目的下的套路卡。
 *
 * ★三层设计 §9「单一真源」:目的来自引擎 8 档 playbook、卡来自 `formulas.yaml`,
 * 前端**不留第二份清单**。加一张卡 / 加一个档,这里零改动。
 */
export interface VideoFormulaCardDto {
  /** 稳定 id(提交时上送的 `formulaKey`)。 */
  key: string;
  name: string;
  /** 什么时候用它(卡面第二行)。 */
  when: string;
  /** 观众动作透镜标(惊艳/购买/收藏/评论/分享/到店/咨询/建立信任)。 */
  drives: string[];
  /** 15s 节拍表(卡面结构预览;含 `{槽位}` 原文,展示层自己处理)。 */
  beats: string[];
  /**
   * B 批(2026-09-13「时长→拍法联动」):各上架时长档的节拍表
   * `{ "15": [...], "30": [...], "45": [...], "60": [...] }`。
   * 派生自引擎 `FormulaCard.beat_plan`(15/30 原生表,45/60 复用缩放)。步骤条按当前时长档
   * 取对应那份;缺该字段(老后端 schemaVersion 1)时回落 `beats`(15s)。**纯附加**,`beats` 不变。
   */
  beatsByTier?: Record<string, string[]>;
  /** 该目的的默认牌:用户不选、且文案无明显特征时引擎会挑这张。 */
  isDefault: boolean;
}

/**
 * 某目的在某时长档下的节拍摘要 —— 「这个档装得下多少东西」的**真值源**(0826 质感批 ⑤)。
 *
 * ★此前前端用 `shotBudget.ts` 的 `CHARS_PER_SELLING_POINT = 45`(一个没有代码出处的经验值)
 * 推「建议讲几个卖点」,45s 推出 5~6 个;而引擎 45s 硬广节拍表只有 **4 个内容位** ——
 * 用户照 UI 写 6 个,引擎按 4 个位子编排,每个都说不透。现在直接读引擎的节拍表。
 */
export interface VideoTaxonomyBeatPlanDto {
  targetSec: number;
  /** 总拍数。 */
  beats: number;
  /** 内容位数(build_tension/turn)= 这个档能讲几个点。 */
  contentBeats: number;
  /** 该档镜数区间(与 ai `shot_budget` 同一次计算,前端不必再复刻公式)。 */
  shotRange: [number, number];
}

export interface VideoTaxonomyPurposeDto {
  /** 规范目的键 = `BRIEF_PURPOSES` 的 label,逐字相同。 */
  purpose: string;
  /** 各上架时长档的节拍摘要。 */
  beatPlans?: VideoTaxonomyBeatPlanDto[];
  /** 该目的在库里没有可选拍法(如「产品硬广」走 feature_forward 直接编排)。 */
  autoOnly: boolean;
  defaultFormulaKey?: string | null;
  /**
   * B 批(2026-09-13):每个时长档的引擎默认牌
   * `{ "15": "pain-triple", "30": "result-first", … }`,复用引擎 `select_formula` 的按时长
   * 选卡逻辑逐档算出。「改时长→推荐拍法自动变」按这张表把该档推荐卡排前/作当前默认卡。
   * 缺该字段(老后端)时回落 `defaultFormulaKey`(30s 那档)。`"30"` 与 `defaultFormulaKey` 同值。
   */
  defaultFormulaByTier?: Record<string, string>;
  formulas: VideoFormulaCardDto[];
}

export interface VideoCreativeTaxonomyResponse {
  schemaVersion: string;
  drives: string[];
  purposes: VideoTaxonomyPurposeDto[];
}

/**
 * 「AI 智能编排(分镜+台本图)」花费区间。字段与 ai 侧 `ComposeEstimateResponse` 逐字同名 ——
 * backend 只透传不改写,console 也别在这层重命名(见 `useComposeEstimate` 头注)。
 */
export interface VideoComposeEstimateResponse {
  low: number;
  high: number;
}

/**
 * 模板类型。`REAL_PERSON`(0924 车 D,契约 §3.4)= 真人出镜样片墙专用:复用
 * `xb_video_template`,只在 `/ai-video/real` 的样片墙出现(`listTemplates({type:"REAL_PERSON"})`),
 * 不是一种视频任务类型 —— 所以不并进 `VideoTaskType`。
 */
export type VideoTemplateType = VideoTaskType | "REAL_PERSON";
export const REAL_PERSON_TEMPLATE_TYPE = "REAL_PERSON" as const;

export interface VideoTemplateDto {
  id: number;
  name: string;
  description?: string;
  coverUrl?: string;
  /**
   * 预览样片(api 由 `preview_video_key` 现签)。★真人样片墙首批 8 条种子该字段为 NULL
   * (样片由主会话后补)—— 缺席时卡面显示「样片制作中」占位,不是错误态。
   */
  previewVideoUrl?: string;
  type: VideoTemplateType;
  industryCodes: string[];
  industryNames?: string[];
  preferredProvider?: VideoProvider;
  preferredModel?: string;
  preferredRatio: string;
  preferredDurationSec: number;
  preferredResolution: VideoResolution;
  preferredGenerateAudio?: boolean;
  briefScript: string;
  formFields?: Array<Record<string, unknown>>;
  /** V46 故事配方:apply 时预填 brief、走两阶故事编排。 */
  briefPurpose?: string;
  briefStyle?: string;
  briefTone?: string;
  briefDuration?: string;
  storyMode?: boolean;
  sortOrder?: number;
  enabled?: boolean;
}

/** 模版填空字段(formFields 元素的强类型视图)。 */
export interface TemplateFormField {
  key: string;
  label: string;
  hint?: string;
  required?: boolean;
  default?: string;
}

/**
 * 「帮我写脚本」(0924 车 D,契约 §3.4):ai `POST /video/real/script`(经 api 转发)。
 * 入参三形态**择一**:`idea`(一句话想法)| `templateId`+`slots`(样片模板 + 槽位)|
 * `blueprintFormula`(追爆蓝图的零逐字套路投影,见 entities/explosive realPersonHandoff)。
 * tenant 由后端从登录态取,console 不送。计费 0(读 = 免费)。
 * 出参 `prompt` = 视频模型直吃的中文提示词,**填进「想拍什么」框给用户看和改**,
 * 「框里是什么就发什么」的直发承诺不变;`draftId` 草稿 Redis 24h,军师卡片
 * `/ai-video/real?draft=<id>` 用 `getRealScriptDraft` 取回。
 */
export interface RealScriptRequest {
  idea?: string;
  templateId?: number;
  slots?: Record<string, string>;
  blueprintFormula?: Record<string, unknown>;
  characterId?: number | null;
  productCode?: string | null;
  durationSec: number;
}

export interface RealScriptResponse {
  prompt: string;
  voiceover?: string | null;
  draftId: string;
}

/**
 * 草稿取回。契约只钉了 prompt/voiceover/draftId;其余是**可选回显**(ai 若把入参一并存了
 * 就带回,没有就缺席)—— 页面只在字段在场时才用它预选人物/时长,缺席不猜。
 */
export interface RealScriptDraftDto extends RealScriptResponse {
  characterId?: number | null;
  durationSec?: number | null;
  templateId?: number | null;
}

export interface VideoPageResponse<T> {
  records: T[];
  total: number;
  page: number;
  size: number;
}

/**
 * 成片重合成选项(结果页「成片编辑」秒切)。字段名对齐后端 VideoComposeOptions /
 * ai-service VideoTaskRequest。全可空:undefined=不改(保持 request_payload 原值)。
 */
export interface VideoComposeOptions {
  voiceEnabled?: boolean;
  subtitleEnabled?: boolean;
  bgmEnabled?: boolean;
  overlayConfig?: OverlayConfig | null;
  /**
   * VID-D(2026-08-18 老板走查「字幕大小位置也放进工作台,甚至加个描边」「能不能在成片上拖」)。
   *
   * 大小/位置引擎早就支持(此前只能在创建时设);描边此前是写死值(720p 2px / 1080p 3px);
   * anchorY/widthPct 是在封面帧上拖出来的 —— **竖向自由 + 宽度手柄,横向恒居中**
   * (放开左右会拖出偏心字幕)。全可空:不传=保持原值,老片重合成不变样。
   */
  subtitleFontScale?: SubtitleFontScale;
  subtitlePosition?: SubtitlePosition;
  subtitleOutline?: SubtitleOutline;
  subtitleAnchorY?: number | null;
  subtitleWidthPct?: number | null;
}

import type {
  SubtitleFontScale,
  SubtitleOutline,
  SubtitlePosition,
} from "@/shared/lib/video/subtitleStyle";

// 档位类型单源在 shared/lib/video/subtitleStyle(0909 数字人重合成共用),这里 re-export 保既有导入。
export type {
  SubtitleFontScale,
  SubtitleOutline,
  SubtitlePosition,
} from "@/shared/lib/video/subtitleStyle";

/** 一段的 prompt 预览(ai `compile_chunk_prompt` 的真产物;与出片发的是同一条)。 */
export interface VideoPromptSegmentDto {
  seq: number;
  /** 本段包含哪几镜(0 基,对齐 panelScenes 下标)。 */
  shotIndexes: number[];
  durationSec: number;
  model: string;
  provider: string;
  continuityMode?: string | null;
  prompt: string;
  chars: number;
  /** 超出本段编译预算 —— 出片时会按反 role 优先级截掉参考派活行。 */
  overBudget: boolean;
  /**
   * 本段的编译预算(0824 起按镜数弹:≤3 镜 500,每多一镜 +150)—— 字数条分母用这个。
   * 老 ai 版本不回此字段 → 退回响应级 promptCharBudget(500)。
   */
  charBudget?: number;
  /**
   * 实发 provider 参数(0824:分镜面板里的景别/运镜等是**编译原料**不是 API 参数,
   * 真正发给 seedance 的这几项在此逐段回报;与渲染路同一批裁决函数产出)。
   * seed 出片时按 (taskId, 段号) 派生,预览期无任务号,不下发。老 ai 不回这些字段。
   */
  generationMode?: string;
  resolution?: string;
  ratio?: string;
  imageRefCount?: number;
  videoRefCount?: number;
  hasAudioRef?: boolean;
  /** 出片前不可知/可能偏离的地方(空 = 这一段的预览就是实发)。 */
  caveats: string[];
}

export interface VideoCompilePreviewResponse {
  schemaVersion: string;
  promptCharBudget: number;
  segments: VideoPromptSegmentDto[];
}

const BASE = "/video";

/**
 * 首帧异步作业:submit 拿 jobId 后短轮询 frame-jobs/{jobId} 直到 SUCCESS/FAILED。
 * 替代旧的同步长请求(单次 48–160s),免疫前置 SLB 的 60s 连接空闲超时(分镜图无法显示根因)。
 * 对调用方透明:仍 resolve {key,url},组件代码无需改。
 */
/** P7(2026-07-12,E10):编排作业轮询——同步 /video/compose 90~150s 被 SLB 掐断(断连白扣钱),
 * 改 submit+poll(shot-frame 同范式)。对调用方透明:仍 resolve storyboard。 */
// 编排 90~150s / 首帧 48–160s 期间要轮询上百次;企微 webview 弱网下单次 poll 网络抖动
// 不能拖垮整个任务(后端 job 仍在跑)。容忍连续瞬时失败到阈值(≈15s 持续断网)才放弃,
// 单次闪断计数复位;只有后端显式 FAILED 或超 deadline 才终止。
const POLL_ERR_TOLERANCE = 6; // 6 × 2.5s ≈ 15s 持续无网才判失败

async function pollComposeJob(jobId: string): Promise<FromPosterBootstrapResponse> {
  const deadline = Date.now() + 5 * 60_000; // 两阶编排 90~150s,留 5min 余量
  let consecutiveErrors = 0;
  while (Date.now() < deadline) {
    let j: { status: string; result?: FromPosterBootstrapResponse; error?: string };
    try {
      j = await api.get(`${BASE}/compose-jobs/${jobId}`);
      consecutiveErrors = 0;
    } catch {
      if (++consecutiveErrors >= POLL_ERR_TOLERANCE) {
        throw new Error("网络不稳定,AI 编排状态获取失败,请重试");
      }
      await new Promise((r) => setTimeout(r, 2500));
      continue;
    }
    if (j.status === "SUCCESS" && j.result) return j.result;
    if (j.status === "FAILED") throw new Error(j.error || "AI 编排失败");
    await new Promise((r) => setTimeout(r, 2500));
  }
  throw new Error("AI 编排超时,请重试");
}

/**
 * 出帧作业轮询。
 *
 * ★`persisted` = 服务端把出好的图写回草稿了没有 —— 决定「不看了」之后那张图还在不在,
 *   进而决定我们该让用户**刷新**(免费)还是**重出**(再扣一次积分)。两条路不同:
 *   - `/drafts/{id}/frames/{seq}/regenerate`:后台线程跑完写回 `panelPreviewKeys[seq]`
 *     (VideoDraftFrameService),重开草稿那条路会按 key 重签回填 ⇒ **刷新就能看到**;
 *   - `/shot-frame`:后端自己注着「不建 task、无 DB 状态变更」,结果只活在这个 job 里,
 *     jobId 随页面一起丢 ⇒ 真的只能重出。
 *   放弃轮询**不会**取消服务端作业(也不会退这笔钱)。出帧 48–160s、窗口 5min,正常用户
 *   很少走到这儿;真容易撞的是代客走查(token 15 分钟过期 → 连续 401 → 下面那条错误分支)。
 *   ——所以这两句话术必须分路写:统一写「请重试」= 叫用户为已经出好的图再付一次。
 */
async function pollFrameJob(
  jobId: string,
  opts: { persisted: boolean }
): Promise<{ key: string; url: string }> {
  const deadline = Date.now() + 5 * 60_000; // 首帧 48–160s,留 5min 余量
  const giveUpHint = opts.persisted
    ? "这张图仍在生成,稍后刷新本页即可看到,不必重出(重出会再扣一次积分)"
    : "请重试";
  let consecutiveErrors = 0;
  while (Date.now() < deadline) {
    let j: { status: string; key?: string; url?: string; error?: string };
    try {
      j = await api.get(`${BASE}/frame-jobs/${jobId}`);
      consecutiveErrors = 0;
    } catch {
      if (++consecutiveErrors >= POLL_ERR_TOLERANCE) {
        throw new Error(`网络不稳定,首帧状态获取失败,${giveUpHint}`);
      }
      await new Promise((r) => setTimeout(r, 2500));
      continue;
    }
    if (j.status === "SUCCESS" && j.key && j.url) return { key: j.key, url: j.url };
    if (j.status === "FAILED") throw new Error(j.error || "首帧生成失败");
    await new Promise((r) => setTimeout(r, 2500));
  }
  throw new Error(`首帧生成超时,${giveUpHint}`);
}

/**
 * 声音组合试听请求(P0.6 0915 车 I:真人页接入声音 V2)。端点
 * `POST /digital-human/voices/preview` 是**声音域通用能力**,不是数字人任务专属——
 * 但这里**不 import** `digitalHumanService`:features 互不 import 是本仓 ESLint 硬闸
 * (`eslint.config.js` features/** no-restricted-imports,error 级),真要 import 会拦提交。
 * 这里镜像声明请求/响应形状直接打同一个端点(与 `DhVoicePreviewRequest` 逐字同形),
 * 不复制任何业务逻辑,两处各自维护但打的是同一个后端契约。
 *
 * ★字段名陷阱(同一个量两个名字,主session 0915 演示机实证):这个端点吃 `speechRate`;
 *   真人页**提交任务**用的是 `voiceSpeechRate`(视频线老字段,合法域 0.9~1.3,契约钉死
 *   绝不可改名)。两个上下文两个名字,故意不统一,调用两处时不要弄混。
 */
export interface VideoVoicePreviewRequest {
  /** 声纹 / 预置音色二选一。 */
  voiceProfileId?: number | null;
  presetVoice?: string | null;
  text: string;
  voiceLanguage: string;
  voiceDialect: string | null;
  voiceStyle: string;
  speechRate?: number | null;
}

export const videoService = {
  getCapabilities: () =>
    api.get<VideoCapabilitiesResponse>(`${BASE}/provider-capabilities`).then((r) => {
      // 后端档位名为准:登记后,作品详情 / 分镜检查器等只拿得到 model 的地方也说同一个名字。
      rememberVideoModelLabels(r?.providers ?? []);
      return r;
    }),

  /** 波2:创建页「选拍法(可选)」卡墙数据(目的 → 套路卡)。静态投影,拿不到就退回「引擎自动」。 */
  /**
   * 出片前 prompt 预览 —— 「我们真正发给视频模型的那条」。只读:不建任务、不出图、不计费。
   *
   * ★入参必须由 `buildTaskRequest` 产出(与 `createTask` 同一份)。另组一套的话,
   *   右栏显示的就不是会真发出去的那条 prompt,而用户会照着它调分镜。见该模块头注。
   */
  compilePreview: (req: VideoTaskCreateRequest) =>
    api.post<VideoCompilePreviewResponse>(`${BASE}/compile-preview`, req),

  /**
   * 0924 车 C:带调性档 `register`(query 名与 ai 对齐),luxury 档 ai 优先推荐质感类拍法。
   * 不传 = 不带 query(ai 缺省 hardsell)。创建页一律经 `useCreativeTaxonomy` 调用。
   */
  getCreativeTaxonomy: (register?: "hardsell" | "restrained" | "luxury" | null) =>
    api.get<VideoCreativeTaxonomyResponse>(
      register
        ? `${BASE}/creative-taxonomy?register=${encodeURIComponent(register)}`
        : `${BASE}/creative-taxonomy`
    ),

  /** 「AI 智能编排」花费区间(console PC 创建页读数口,与军师确认卡同一个 ai 侧常量出处)。 */
  getComposeEstimate: () => api.get<VideoComposeEstimateResponse>(`${BASE}/compose/estimate`),

  createTask: (req: VideoTaskCreateRequest) => api.post<{ taskId: number }>(`${BASE}/tasks`, req),

  /**
   * 任务列表。2026-07-15 三线列表对齐:补 q(任务名搜索,后端 name LIKE 全表搜 —— 不是前端只过滤
   * 当前页,那样翻页外的命中会漏)。
   *
   * <p>2026-07-16 补 `bucket` 到类型里:后端一直支持(running=PENDING|RUNNING / history=终态),
   * 但此前前端是靠 `...filterParams(f)` **展开**传进来的 —— 展开不触发 TS 的多余属性检查,
   * 于是运行时能用、类型里却没有。三段式布局要显式传 bucket,顺手把这个类型窟窿补上。
   */
  listTasks: (
    params: {
      /** B6 场景筛(catalog sceneKey;缺省=不筛)。 */
      sceneKey?: string;
      /** 1102 只看收藏(true 才下推)。 */
      favorite?: boolean;
      type?: string;
      status?: string;
      /** running = PENDING|RUNNING(进行中节);history = 终态(历史节)。 */
      bucket?: "running" | "history";
      provider?: string;
      mineOnly?: boolean;
      q?: string;
      page?: number;
      size?: number;
    } & OrgScopeParams = {}
  ) => {
    // #9 组织级归属筛选(scope/departmentId/memberId);scope=all 不下推(等价默认 = 授权天花板)。
    const { scope, ...rest } = params;
    const qs = new URLSearchParams();
    Object.entries(rest).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
    });
    if (scope && scope !== "all") qs.set("scope", scope);
    const suffix = qs.toString() ? `?${qs}` : "";
    return api.get<VideoPageResponse<VideoTaskDto>>(`${BASE}/tasks${suffix}`);
  },

  getTask: (id: number) => api.get<VideoTaskDto>(`${BASE}/tasks/${id}`),

  cancelTask: (id: number) => api.post<void>(`${BASE}/tasks/${id}/cancel`, {}),

  deleteTask: (id: number) => api.delete<void>(`${BASE}/tasks/${id}`),

  /**
   * 改任务元数据(收藏 / 重命名);2026-07-15 三线列表对齐补齐(此前视频卡只有删除,没有收藏/重命名)。
   * 对齐 posterService.updateTaskMeta / digitalHumanService.updateTaskMeta。
   */
  updateTaskMeta: (id: number, req: UpdateVideoTaskMetaRequest) =>
    api.patch<VideoTaskDto>(`${BASE}/tasks/${id}`, req),

  retryTask: (id: number) => api.post<{ taskId: number }>(`${BASE}/tasks/${id}/retry`, {}),

  /** 多镜成片里单独重渲第 seq 镜(1-based)。edits 非空先持久化编辑;只重渲该镜不自动合成。 */
  regenerateShot: (taskId: number, seq: number, edits?: VideoShotEdit) =>
    api.post<void>(`${BASE}/tasks/${taskId}/assets/${seq}/regenerate`, edits ?? {}),

  /** 单镜编辑(只持久化文本字段,不重渲)。null 字段=不改。 */
  editShot: (taskId: number, seq: number, edits: VideoShotEdit) =>
    api.post<void>(`${BASE}/tasks/${taskId}/assets/${seq}/edit`, edits),

  /** 删除第 seq 镜:逐镜数据 + SHOT 资产同步删,后续镜 seq 顺延,成片作废需重合成。至少保留 2 镜。 */
  deleteShot: (taskId: number, seq: number) =>
    api.delete<void>(`${BASE}/tasks/${taskId}/shots/${seq}`),

  /** 合成成片:把当前全 N 镜 SHOT concat 成 FINAL。返回后任务翻 RENDERING,详情页轮询刷新。 */
  composeFinal: (taskId: number, options?: VideoComposeOptions) =>
    api.post<void>(`${BASE}/tasks/${taskId}/compose-final`, options ?? {}),

  /** chunk 编排任务单段重渲(P2d-3):用前段已存尾帧承接,只重渲第 seq 段(1-based)。不自动合成。 */
  /**
   * 重渲某段。★**不收 edits**:后端这个入参是死参(收下即丢),留着会让调用方以为
   * "改文案 + 重渲"能一次带过去,实际上文案原样、钱照花。要改文案先 editShot 再来调这个。
   */
  regenerateChunk: (taskId: number, seq: number) =>
    api.post<void>(`${BASE}/tasks/${taskId}/chunks/${seq}/regenerate`, {}),

  /** PARTIAL chunk 任务续渲(P2d-4):从断点段补齐缺失段并拼全片。返回后任务翻 RENDERING,轮询刷新。 */
  resume: (taskId: number) => api.post<void>(`${BASE}/tasks/${taskId}/resume`, {}),

  getDownloadUrl: (assetId: number) =>
    api.post<{ url: string }>(`${BASE}/assets/${assetId}/download`, {}),

  listTemplates: (opts: { type?: string; industryCode?: string } = {}) => {
    const qs = new URLSearchParams();
    if (opts.type) qs.set("type", opts.type);
    if (opts.industryCode) qs.set("industryCode", opts.industryCode);
    const suffix = qs.toString() ? `?${qs}` : "";
    return api.get<VideoTemplateDto[]>(`${BASE}/templates${suffix}`);
  },

  getTemplate: (id: number) => api.get<VideoTemplateDto>(`${BASE}/templates/${id}`),

  /** 真人出镜「帮我写脚本」(0924 车 D)—— 见 `RealScriptRequest` 头注。 */
  writeRealScript: (req: RealScriptRequest) =>
    api.post<RealScriptResponse>(`${BASE}/real/script`, req),

  /** 取回脚本草稿(`?draft=` 通道;军师「当编剧」卡片带来的 id)。 */
  getRealScriptDraft: (draftId: string) =>
    api.get<RealScriptDraftDto>(`${BASE}/real/script/${encodeURIComponent(draftId)}`),

  /**
   * 源海报任务的作品名(1006:横幅「海报「…」」在装配响应没有内容标题时退回作品名)。
   * 只读 `name` 一个字段;取不到 → null,横幅照旧占位,不挡装配。
   */
  getPosterTaskName: (taskId: number) =>
    api
      .get<{ name?: string | null }>(`/poster/tasks/${taskId}`)
      .then((t) => t?.name ?? null)
      .catch(() => null),

  bootstrapFromPoster: (
    taskId: number,
    assetSeq?: number,
    opts?: { userInput?: string; optimize?: boolean }
  ) => {
    const qs = new URLSearchParams({ taskId: String(taskId) });
    if (assetSeq != null) qs.set("assetSeq", String(assetSeq));
    if (opts?.userInput && opts.userInput.trim()) qs.set("userInput", opts.userInput.trim());
    if (opts?.optimize) qs.set("optimize", "true");
    return api.get<FromPosterBootstrapResponse>(`${BASE}/from-poster?${qs}`);
  },

  /** V42 上传首/尾帧图(I2V/I2V_FIRST_LAST 模式用),返 url+key。 */
  /**
   * T2A.6a 上传图分类:产品/角色图 key(uploadFrame 落 UPLOADS 桶)→ ai 实体表({entities}),
   * compose 前调、交确认单展示「产品=图X」映射。tenant 后端取 TenantContext,不随请求送。
   * 1005:有草稿时带 draftId → api 账单 actionRef 挂 draft:{id}(积分明细能对回这条草稿);没草稿不带。
   */
  classifyImages: (keys: string[], primaryProduct?: string, draftId?: number | null) =>
    api.post<{ entities: VideoEntity[] }>(`${BASE}/classify-images`, {
      keys,
      ...(primaryProduct ? { primaryProduct } : {}),
      ...(typeof draftId === "number" && draftId > 0 ? { draftId } : {}),
    }),

  uploadFrame: (file: File): Promise<{ url: string; key: string }> => {
    const form = new FormData();
    form.append("file", file);
    return api.post<{ url: string; key: string }>(`${BASE}/uploads/frame`, form);
  },

  /** 新主线:纯文案「AI 智能编排」— LLM 据用户文案 + 预填 brief 产单/多镜 storyboard。 */
  composeFromText: async (body: {
    userInput: string;
    style?: string;
    purpose?: string;
    durationTier?: string;
    tone?: string;
    brandName?: string;
    storyMode?: boolean;
  }): Promise<FromPosterBootstrapResponse> => {
    // 异步:提交拿 jobId,再轮询取 storyboard(每次请求 <1s,免疫 SLB 空闲超时)。
    const { jobId } = await api.post<{ jobId: string }>(`${BASE}/compose-submit`, body);
    return pollComposeJob(jobId);
  },

  /** 新主线:按某镜画面描述同步生成一张首帧图(gpt-image-2,有 anchorKey 走 i2i 保一致),返 key+url。 */
  // 契约 v2 起不再传 artDirection:导演已不产美术圣经(风格由用户选的 styleCode 一次定调),
  // 前端手上恒为空 —— 留着这个入参 = 永远传 undefined 的死参。跨镜一致改由 anchorKey 锚点承担。
  generateShotFrame: async (body: {
    scenePrompt: string;
    ratio?: string;
    anchorKey?: string;
  }): Promise<{ key: string; url: string }> => {
    // 异步:提交拿 jobId,再轮询取帧(每次请求 <1s,不再被 SLB 60s 掐断)。
    const { jobId } = await api.post<{ jobId: string }>(`${BASE}/shot-frame`, body);
    // 后端 @PostMapping("/shot-frame") 自注「不建 task、无 DB 状态变更」= 不写回草稿。
    return pollFrameJob(jobId, { persisted: false });
  },

  /** V42 上传自定义 BGM mp3,返 objectKey(用于 task 创建时传 customBgmObjectKey)+ url(试听)。 */
  uploadCustomBgm: (file: File): Promise<{ objectKey: string; url: string; sizeBytes: string }> => {
    const form = new FormData();
    form.append("file", file);
    return api.post<{ objectKey: string; url: string; sizeBytes: string }>(
      `${BASE}/uploads/bgm`,
      form
    );
  },

  /** V42 LLM 文案优化(有 posterPrompt 自动走融合模式)。 */
  optimizePrompt: (
    userInput: string,
    posterPrompt?: string
  ): Promise<{ optimizedPrompt: string }> =>
    api.post<{ optimizedPrompt: string }>(`${BASE}/llm/prompt-optimize`, {
      userInput,
      posterPrompt,
    }),

  /**
   * 声音 V2 组合试听(P0.6 0915 车 I):按「声纹/预置音色 × 语言 × 方言 × 演绎 × 语速」
   * 现合成一小段,返可播的 audioUrl。★端点不带 `${BASE}`——它挂在 `/digital-human` 域
   * 下(见 `VideoVoicePreviewRequest` 头注,声音域通用能力,与视频任务的 `/video/*`
   * 是两个不同域)。
   */
  previewVoice: (req: VideoVoicePreviewRequest) =>
    api.post<{ audioUrl: string }>("/digital-human/voices/preview", req),

  // V42 BGM
  listBgms: () => api.get<VideoBgmDto[]>(`${BASE}/bgms`),

  listAllBgms: () => api.get<VideoBgmDto[]>(`${BASE}/platform/bgms`),

  createBgm: (params: {
    name: string;
    tags?: string[];
    enabled?: boolean;
    sortOrder?: number;
    mp3: File;
  }) => {
    const fd = new FormData();
    fd.append("name", params.name);
    if (params.tags) for (const t of params.tags) fd.append("tags", t);
    if (params.enabled !== undefined) fd.append("enabled", String(params.enabled));
    if (params.sortOrder !== undefined) fd.append("sortOrder", String(params.sortOrder));
    fd.append("mp3", params.mp3);
    return api.post<VideoBgmDto>(`${BASE}/platform/bgms`, fd);
  },

  updateBgm: (
    id: number,
    params: {
      name?: string;
      tags?: string[];
      enabled?: boolean;
      sortOrder?: number;
      mp3?: File;
    }
  ) => {
    const fd = new FormData();
    if (params.name) fd.append("name", params.name);
    if (params.tags) for (const t of params.tags) fd.append("tags", t);
    if (params.enabled !== undefined) fd.append("enabled", String(params.enabled));
    if (params.sortOrder !== undefined) fd.append("sortOrder", String(params.sortOrder));
    if (params.mp3) fd.append("mp3", params.mp3);
    return api.post<VideoBgmDto>(`${BASE}/platform/bgms/${id}`, fd);
  },

  deleteBgm: (id: number) => api.delete<void>(`${BASE}/platform/bgms/${id}`),

  // ───── 视频创作草稿(编排/优化后自动 upsert + 手动保存,继续编辑恢复 form) ─────
  listDrafts: () => api.get<VideoDraftDto[]>(`${BASE}/drafts`),
  getDraft: (id: number) => api.get<VideoDraftDto>(`${BASE}/drafts/${id}`),
  upsertDraft: (req: UpsertVideoDraftRequest) => api.post<VideoDraftDto>(`${BASE}/drafts`, req),
  deleteDraft: (id: number) => api.delete<void>(`${BASE}/drafts/${id}`),
  /** 草稿恢复:按永久首帧 key 重签 24h URL(草稿里的 URL 会过期),返 key→url。 */
  presignFrames: (keys: string[]) =>
    api.post<Record<string, string>>(`${BASE}/frames/presign`, { keys }),
  /** 服务端「编排+出图」整条流:草稿只有 brief 时触发,worker 先 LLM 编排写回 storyboard 再出图。
   *  前端轮询 getDraft:COMPOSING → GENERATING_FRAMES → DONE,先看到大纲、再看到帧。 */
  composeAndGenerateDraft: (draftId: number) =>
    api.post<void>(`${BASE}/drafts/${draftId}/compose-and-generate`, {}),
  /** 重渲草稿里的某一镜(同步,返新 key+url)。 */
  regenerateDraftFrame: async (
    draftId: number,
    seq: number
  ): Promise<{ key: string; url: string }> => {
    // 异步:同 generateShotFrame,提交 + 轮询。
    const { jobId } = await api.post<{ jobId: string }>(
      `${BASE}/drafts/${draftId}/frames/${seq}/regenerate`,
      {}
    );
    // 后台线程跑完写回草稿 panelPreviewKeys[seq] ⇒ 放弃轮询后刷新仍能捡回来。
    return pollFrameJob(jobId, { persisted: true });
  },
};

/** 视频创作草稿 DTO(formJson = 整个创建 FormState 的 JSON 快照)。 */
export interface VideoDraftDto {
  draftId: number;
  name: string;
  formJson: string;
  /** 首帧出图 job 状态:IDLE/RUNNING/DONE/PARTIAL/FAILED。前端据此轮询/收尾。 */
  frameGenStatus?: string;
  /** 列表卡片封面:草稿首个已出分镜首帧 presigned URL(无则空,卡片走占位)。 */
  coverThumbUrl?: string;
  createdAt?: string;
  updatedAt?: string;
  /**
   * 这份草稿**已经提交出片**时,那一单的任务 id(没提交过=null)。
   *
   * 后端在成片真正成功时会软删来源草稿,失败/部分失败才保留(供重渲或重试)。问题出在中间那段窗口:
   * 视频从提交到成片要好几分钟,这期间草稿仍在草稿箱里、且一声不吭 —— 0813 爱民
   * 「视频已经在制作了,但是还在草稿箱里」。有了它,草稿箱这段时间能说出实话并给去处。
   */
  consumedTaskId?: number | null;
  /**
   * 本次 `frameGenStatus=COMPOSE_FAILED` 的**钱口径**(VID-REFUND-1);其余状态为空。
   *
   * `EMPTY` = ai 明确算出空产出(ai 侧票内抛错、未 settle → 用户确实没被扣);
   * `TIMEOUT`/`TRANSPORT` = 没等到 ai 回包,而 ai 很可能已成功收尾并 settle →
   * **钱很可能已经扣了,且用现有机制退不回**;`UPSTREAM_ERROR` = ai 明确回错,票已按失败退掉。
   *
   * ★这几种此前全被压成同一个 COMPOSE_FAILED,前端只能说同一句话 —— 那句话是「未产生出图扣费」,
   * 对 TIMEOUT 那一半就是**反着说的**。不许再对全体宣称没扣费。
   */
  composeFailKind?: string | null;
}

/** 草稿 upsert body。draftId 为空=新建,非空=更新。 */
export interface UpsertVideoDraftRequest {
  draftId?: number | null;
  name: string;
  formJson: string;
}

/** V42 BGM DTO。 */
export interface VideoBgmDto {
  id: number;
  name: string;
  durationSec: number;
  url?: string;
  tags?: string[];
  enabled?: boolean;
  sortOrder?: number;
}
